import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import ReplyIcon from '@mui/icons-material/Reply';
import RestoreIcon from '@mui/icons-material/Restore';
import { Divider, IconButton, ListItemIcon, Menu, MenuItem } from '@mui/material';
import { DeleteEventDialog } from 'components/event/dialog/DeleteEventDialog';
import { ShareEventDialog } from 'components/event/dialog/ShareEventDialog';
import { useLocalPlaylist } from 'hooks/useLocalPlaylist';
import { usePlaylistExport } from 'hooks/usePlaylistExport';
import { useUser } from 'hooks/useUser';
import { EventOccasion } from 'models/EventOccasion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export interface EventActionMenuProps {
  event: EventOccasion;
  onDeleteEvent?: (event: EventOccasion) => void;
  onRestoreEvent?: (event: EventOccasion) => void;
  onOpenDialog?: () => void;
  onCloseDialog?: () => void;
}

export function EventActionMenu(props: EventActionMenuProps) {
  const { t } = useTranslation();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | Element>(null);
  const openMenu = Boolean(menuAnchorEl);
  const { user } = useUser();
  const { getLocalPlaylist } = useLocalPlaylist();
  const { createPlaylistDefault, getDefaultPlaylistType } = usePlaylistExport(props.event);

  const handleOpenMenu = (ev: React.MouseEvent<Element>) => {
    ev.preventDefault();
    setMenuAnchorEl(ev.currentTarget);
    props.onOpenDialog?.();
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);

    if (!openDeleteDialog) {
      props.onCloseDialog?.();
    }
  };

  const handleOpenShareDialog = (ev: React.MouseEvent<Element>) => {
    ev.preventDefault();
    handleCloseMenu();
    setOpenShareDialog(true);
    props.onOpenDialog?.();
  };

  const handleCloseShareDialog = (ev?: React.MouseEvent<Element>) => {
    ev?.preventDefault();
    handleCloseMenu();
    setOpenShareDialog(false);
    props.onCloseDialog?.();
  };

  const handleOpenDeleteDialog = (ev: React.MouseEvent<Element>) => {
    handleCloseMenu();
    setOpenDeleteDialog(true);
    props.onOpenDialog?.();
  };

  const handleCloseDeleteDialog = (ev?: React.MouseEvent<Element>) => {
    ev?.preventDefault();
    handleCloseMenu();
    setOpenDeleteDialog(false);
    props.onCloseDialog?.();
  };

  const handleDeleteEvent = () => {
    props.onDeleteEvent?.(props.event);
    handleCloseMenu();
    handleCloseDeleteDialog();
  };

  const handleRestoreEvent = () => {
    handleCloseMenu();
    props.onRestoreEvent?.(props.event);
  };

  const handleExportPlaylist = async () => {
    if (props.event.requests) {
      const localPlaylist = await getLocalPlaylist(props.event.requests);
      const content = createPlaylistDefault(localPlaylist);

      if (!content) return;

      // createDownloadFile(content, props.event.name, getDefaultPlaylistType());

      // if (playlist) {
      //   const playlistM3u = createPlaylistM3u(playlist);

      //   if (playlistM3u) {
      //     createDownloadFile(playlistM3u, `${props.event.name}.m3u`);
      //   }
      // }
    }

    handleCloseMenu();
  };

  return (
    <>
      <IconButton aria-label="settings" onClick={handleOpenMenu} component="a">
        <MoreVertRoundedIcon />
      </IconButton>
      <Menu
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={menuAnchorEl}
        open={openMenu}
        onClose={handleCloseMenu}
      >
        <MenuItem component={Link} to={`/events/${props.event.id}`}>
          <ListItemIcon>
            <EditRoundedIcon />
          </ListItemIcon>
          {t('edit', 'Edit')}
        </MenuItem>
        <MenuItem onClick={handleOpenShareDialog}>
          <ListItemIcon>
            <ReplyIcon sx={{ transform: 'scaleX(-1)' }} />
          </ListItemIcon>
          {t('share', 'Share')}
        </MenuItem>
        {/* <MenuItem onClick={handleExportPlaylist}>
          <ListItemIcon>
            <QueueMusicIcon />
          </ListItemIcon>
          {t('exportPlaylist', 'Export Playlist')}
        </MenuItem> */}
        <Divider />
        {props.event.isDeleted && (
          <MenuItem onClick={handleRestoreEvent}>
            <ListItemIcon>
              <RestoreIcon />
            </ListItemIcon>
            {t('restore', 'Restore')}
          </MenuItem>
        )}
        {!props.event.isDeleted && (
          <MenuItem onClick={handleOpenDeleteDialog} color="error">
            <ListItemIcon>
              <DeleteOutlineRoundedIcon />
            </ListItemIcon>
            {t('delete', 'Delete')}
          </MenuItem>
        )}
      </Menu>
      <DeleteEventDialog
        event={props.event}
        open={openDeleteDialog}
        onDeleteEvent={handleDeleteEvent}
        onClose={handleCloseDeleteDialog}
      />
      {openShareDialog && props.event && (
        <ShareEventDialog
          eventId={props.event.id}
          open={openShareDialog}
          onClose={handleCloseShareDialog}
        />
      )}
    </>
  );
}
